import { Button } from '@/components/ui/button';
import { LeadCustomerDetails } from '@/components/ui/leads/lead-customer-details';
import LeadLine from '@/components/ui/leads/lead-line';
import {
  FEATURE_FLAG_NAMES,
  useIsFeatureEnabled,
} from '@/hooks/use-feature-flags';
import { toast } from '@/hooks/use-toast';
import { useUser } from '@/hooks/use-user';
import { pdkAxios } from '@/lib/pdk-axios';
import { type CustomerLead } from '@/models/registry';
import { leadStore } from '@/models/stores/leads';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Droplets, RefreshCcwDot, User, Users } from 'lucide-react';
import { observer } from 'mobx-react';
import { useMemo, useState } from 'react';

export const RouteComponent = observer(() => {
  const [filterMyLeadsOnly, setFilterMyLeadsOnly] = useState(false);
  const user = useUser();

  const { isFeatureEnabled: isFullRefreshEnabled } = useIsFeatureEnabled({
    featureFlagName: FEATURE_FLAG_NAMES.FULL_LEADS_REFRESH,
  });

  const fullRefreshMutation = useMutation({
    mutationFn: async () => {
      await pdkAxios.post('/leads/refresh');
    },
  });

  const consolidatedCustomers = leadStore.all;

  const [selectedCustomer, setSelectedCustomer] = useState<CustomerLead | null>(
    null,
  );

  const filteredLeads = useMemo(() => {
    if (filterMyLeadsOnly) {
      return consolidatedCustomers.filter(
        (customer) => customer.original_sales_rep_id === user.id,
      );
    }
    return consolidatedCustomers;
  }, [consolidatedCustomers, filterMyLeadsOnly, user.id]);

  const sortedFilteredLeads = useMemo(() => {
    return [...filteredLeads].sort((a, b) => b.priority - a.priority);
  }, [filteredLeads]);

  const handleFilterMyLeadsOnly = (b: boolean) => {
    if (b) {
      setFilterMyLeadsOnly(true);
    } else {
      setFilterMyLeadsOnly(false);
    }
  };

  const handleFullRefreshLeads = () => fullRefreshMutation.mutate();

  const handleRegenerateLeads = () => {
    toast({
      title: 'Regenerating leads...',
      description: 'This may take a few seconds...',
    });
    leadStore.hydrateRefresh();
  };

  return (
    <div className="flex flex-col bg-white h-screen">
      <div className="flex flex-row gap-2 items-center p-2 border-b justify-between">
        <div className="flex flex-row gap-2">
          <Button
            onClick={() => handleFilterMyLeadsOnly(false)}
            variant="ghost"
            className={`p-1 h-fit border rounded-md mr-2 ${!filterMyLeadsOnly ? 'bg-gray-100' : ''}`}
          >
            <Users className="size-3" />
            <span className="text-xs  text-gray-500">All Leads</span>
          </Button>
          <Button
            onClick={() => handleFilterMyLeadsOnly(true)}
            variant="ghost"
            className={`p-1 h-fit border rounded-md mr-2 ${filterMyLeadsOnly ? 'bg-gray-100' : ''}`}
          >
            <User className="size-3" />
            <span className="text-xs  text-gray-500">My Leads</span>
          </Button>
        </div>
        <div className="flex flex-row gap-2">
          <Button
            disabled={!isFullRefreshEnabled}
            onClick={handleFullRefreshLeads}
            variant="ghost"
            className="p-1 h-fit border rounded-md mr-2"
          >
            <Droplets className="size-3" />
            <span className="text-xs  text-gray-500">Full Refresh</span>
          </Button>
          <Button
            onClick={handleRegenerateLeads}
            variant="ghost"
            className="p-1 h-fit border rounded-md mr-2"
          >
            <RefreshCcwDot className="size-3" />
            <span className="text-xs  text-gray-500">Regenerate List</span>
          </Button>
        </div>
      </div>

      {consolidatedCustomers && consolidatedCustomers.length > 0 ? (
        <div className="flex flex-1 overflow-hidden">
          {/* Customer List - Left Side */}
          <div className="w-1/2 border-r bg-gray-50 overflow-y-auto">
            <div>
              {sortedFilteredLeads.map((customer) => (
                <LeadLine
                  key={customer.customer_id}
                  lead={customer}
                  onClick={() => setSelectedCustomer(customer)}
                />
              ))}
            </div>
          </div>

          {/* Customer Details - Right Side */}
          <div className="flex-1 p-6 overflow-y-auto">
            <div className="flex-1 p-6 overflow-y-auto">
              {selectedCustomer ? (
                <LeadCustomerDetails selectedCustomer={selectedCustomer} />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-gray-500 text-xl">
                    Select a lead to get started
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center h-full">
          <p className="text-gray-500 text-xl">
            Hydrate leads to see them here
          </p>
        </div>
      )}
    </div>
  );
});

export const Route = createFileRoute('/(authenticated)/leads')({
  component: RouteComponent,
  staticData: {
    keepAlive: true,
  },
});
